import { StaticGuestFloorPlan } from 'webReservations/components/StaticGuestFloorPlan';
import { Unauthenticated } from 'webReservations/components/Unauthenticated';
import { useRestaurantContext } from 'webReservations/context/RestaurantContext';
import { useUserContext } from 'webReservations/context/UserContext';
import { useTopOffer } from 'webReservations/hooks/useTopOffer';
import { Layout } from 'webReservations/layout/Layout';
import { getReservationStatusLabel } from 'webReservations/reservations/statusHelpers';
import { reservationTableDisplayName } from 'webReservations/utils/reservationTableDisplayName';
import { SnapshotFloorPlan } from '@components/floorPlan/SnapshotFloorPlan';
import { LoadingPage } from '@components/LoadingPage';
import { useDocumentTitle } from '@shared/hooks/setDocumentTitle';
import { useParams } from 'react-router-dom';
import { NotFoundPage } from '../errors/NotFound/NotFoundPage';
import { useReservationDetails } from '../hooks/useReservationDetails';
import { DetailsPageView } from './DetailsPageView';
import {
  resolveEventAddressText,
  resolveEventDirectionsText,
} from './eventLocationDisplay';
import { ReservationDetailsPanel } from './ReservationDetailsPanel';

export const ReservationDetailsPage = () => {
  const { restaurantDetails } = useRestaurantContext();
  const { reservationId } = useParams();
  const { isAuthenticated } = useUserContext();
  useDocumentTitle('My Reservation Details | Peak Reservations');

  const {
    data: reservation,
    isPending: isReservationLoading,
    refetch,
  } = useReservationDetails();
  const { data: topOffer } = useTopOffer(reservation);

  if (!isAuthenticated) {
    return (
      <Layout>
        <Unauthenticated title="Log in to view your reservations" subtitle="" />
      </Layout>
    );
  }

  if (isReservationLoading) {
    return <LoadingPage />;
  }

  if (!reservation || !reservationId) {
    return <NotFoundPage />;
  }

  const { floorPlan } = reservation;

  const isEventReservation = Boolean(reservation.restaurantEvent);
  const eventAddressText = resolveEventAddressText(reservation);
  const restaurantForHero =
    isEventReservation && eventAddressText
      ? { ...reservation.restaurant, address: eventAddressText }
      : reservation.restaurant;

  return (
    <DetailsPageView
      floorPlanComponent={
        reservation.floorPlanSnapshotHash ? (
          <SnapshotFloorPlan reservationId={reservationId} />
        ) : (
          <StaticGuestFloorPlan
            backgroundSrc={floorPlan?.backgroundSrc}
            tables={floorPlan?.floorPlanTables || []}
          />
        )
      }
      status={getReservationStatusLabel(
        reservation,
        restaurantDetails.timezone,
      )}
      restaurant={restaurantForHero}
      hideAddress={isEventReservation && !eventAddressText}
      directionsText={resolveEventDirectionsText(reservation)}
      date={reservation.date}
      guestCount={reservation.guestCount}
      listingPublicName={reservationTableDisplayName(reservation)}
      time={reservation.time}
    >
      <ReservationDetailsPanel
        fetchReservation={refetch}
        reservation={reservation}
        reservationId={reservationId}
        topOffer={topOffer}
      />
    </DetailsPageView>
  );
};
